/**
 * States of an object shown on its card, in its header and above the
 * sections (TZ-UI «Стани»): Phage.Energy's access to the station (D4: data
 * and control go through it) and the inverter connection; the history
 * download.
 */
import type { PhageAccessStatus, SiteAnalysisResponse, SiteOverviewResponse, SiteResponse } from 'shared';

/** The poller reads every 5 minutes; no sample for this long — «Немає зв'язку». */
export const OFFLINE_AFTER_MS = 20 * 60_000;
/** Depth of the history download (`HISTORY_DEPTH_DAYS`, 4-day windows). */
export const HISTORY_DAYS = 90;
/** Complete days the profile and the potential need. */
export const DAYS_NEEDED = 30;

/**
 * `pending` / `closed` — no access of Phage.Energy: no data, no control;
 * `noedit` — data comes, no right to manage; `down` — DeyeCloud does not
 * answer (or answers an error); `off` — DeyeCloud answers, the inverter
 * sends no data.
 */
export type ConnState = 'ok' | 'off' | 'never' | 'down' | 'pending' | 'closed' | 'noedit';

export interface ConnInput {
  access: PhageAccessStatus;
  vendor_down_since: string | null;
}

/** The connection input of an object response. */
export const connOf = (site: Pick<SiteResponse, 'phage_access' | 'vendor_down_since'>): ConnInput => ({
  access: site.phage_access.status,
  vendor_down_since: site.vendor_down_since,
});

export function connState(
  site: ConnInput,
  latest: SiteOverviewResponse['latest'] | undefined,
  now = Date.now(),
): ConnState {
  if (site.access === 'pending') return 'pending';
  if (site.access === 'closed') return 'closed';
  if (site.vendor_down_since) return 'down';
  if (!latest) return 'never';
  if (now - Date.parse(latest.ts) > OFFLINE_AFTER_MS) return 'off';
  return site.access === 'noedit' ? 'noedit' : 'ok';
}

export type HistState =
  | { kind: 'done' }
  | { kind: 'pending' }
  | { kind: 'loading'; done: number }
  | { kind: 'few'; days: number }
  | { kind: 'error' };

/**
 * `potentialOk` — the potential is computed: then fewer complete days (a gap
 * in the history) are not «мало даних», which says the potential isn't counted.
 */
export function histState(a: SiteAnalysisResponse | undefined, potentialOk = false): HistState | null {
  if (!a) return null;
  if (a.status === 'failed') return { kind: 'error' };
  if (a.status === 'pending' && a.windows_done === 0) return { kind: 'pending' };
  if (a.status === 'pending' || a.status === 'running') {
    const done = a.windows_total > 0 ? Math.round((HISTORY_DAYS * a.windows_done) / a.windows_total) : 0;
    return { kind: 'loading', done: Math.min(HISTORY_DAYS, done) };
  }
  const days = a.last_30_days?.days ?? 0;
  return days < DAYS_NEEDED && !potentialOk ? { kind: 'few', days } : { kind: 'done' };
}
